// =====================================================================
// EGL v12 Frontend - SESSION EXTENSION MODAL
// Purpose: Allow users to extend their session before expiry
// =====================================================================

import React, { useState, useEffect } from 'react'
import { TokenManager } from '../lib/tokenManager'
import './SessionExtensionModal.css'

interface SessionExtensionModalProps {
  isOpen: boolean
  username: string
  timeLeft: string
  onExtend: (password: string) => Promise<void>
  onCancel: () => void
  onLogout: () => void
}

interface ExtensionState {
  password: string
  isExtending: boolean
  error: string | null
  showPasswordField: boolean
}

interface CountdownState {
  currentTimeLeft: string
  isExpired: boolean
}

export const SessionExtensionModal: React.FC<SessionExtensionModalProps> = ({
  isOpen,
  username,
  timeLeft,
  onExtend,
  onCancel,
  onLogout
}) => {
  const [extensionState, setExtensionState] = useState<ExtensionState>({
    password: '',
    isExtending: false,
    error: null,
    showPasswordField: false
  })

  const [countdownState, setCountdownState] = useState<CountdownState>({
    currentTimeLeft: timeLeft,
    isExpired: false
  })

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setExtensionState({
        password: '',
        isExtending: false,
        error: null,
        showPasswordField: false
      })
      setCountdownState({
        currentTimeLeft: timeLeft,
        isExpired: false
      })
    }
  }, [isOpen, timeLeft])

  // Real-time countdown timer
  useEffect(() => {
    if (!isOpen) return

    const countdownInterval = setInterval(() => {
      const currentTimeLeft = TokenManager.getTimeUntilExpiry()
      const isExpired = TokenManager.isExpired()
      
      setCountdownState({
        currentTimeLeft,
        isExpired
      })

      // Auto-close modal if session expired
      if (isExpired) {
        console.log('🔍 Session expired during countdown, triggering logout')
        onLogout()
      }
    }, 1000) // Update every second for real-time countdown

    return () => clearInterval(countdownInterval)
  }, [isOpen, onLogout])

  // Focus password field when it becomes visible
  useEffect(() => {
    if (extensionState.showPasswordField) {
      const passwordField = document.getElementById('extension-password')
      if (passwordField) {
        setTimeout(() => passwordField.focus(), 100)
      }
    }
  }, [extensionState.showPasswordField])

  const handleExtendClick = () => {
    setExtensionState(prev => ({ 
      ...prev, 
      showPasswordField: true,
      error: null 
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!extensionState.password.trim()) {
      setExtensionState(prev => ({ ...prev, error: 'Password is required' }))
      return
    }

    setExtensionState(prev => ({ 
      ...prev, 
      isExtending: true, 
      error: null 
    }))

    try {
      await onExtend(extensionState.password)
      // Success is handled by parent component
    } catch (error) {
      setExtensionState(prev => ({
        ...prev,
        isExtending: false,
        error: error instanceof Error ? error.message : 'Extension failed'
      }))
    }
  }

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setExtensionState(prev => ({ 
      ...prev, 
      password: e.target.value,
      error: null
    }))
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !extensionState.isExtending) {
      handleSubmit(e as any)
    }
  }

  if (!isOpen) return null

  return (
    <div className="session-extension-overlay">
      <div className="session-extension-modal">
        <div className="session-extension-header">
          <h3>⏰ Session Expiring Soon</h3>
          <p>Your session expires in <strong className={countdownState.isExpired ? 'expired' : ''}>{countdownState.currentTimeLeft}</strong></p>
          {countdownState.isExpired && (
            <p className="expired-warning">⚠️ Session has expired!</p>
          )}
        </div>

        {!extensionState.showPasswordField ? (
          // Initial state - just show extend button
          <div className="session-extension-content">
            <p>Would you like to extend your session for another 15 minutes?</p>
            
            <div className="session-extension-actions">
              <button
                type="button"
                onClick={handleExtendClick}
                className="session-extend-btn"
              >
                🔄 Extend Session
              </button>
              
              <button
                type="button"
                onClick={onCancel}
                className="session-cancel-btn"
              >
                Continue Working
              </button>
              
              <button
                type="button"
                onClick={onLogout}
                className="session-logout-btn"
              >
                Logout Now
              </button>
            </div>
          </div>
        ) : (
          // Password entry state
          <form onSubmit={handleSubmit} className="session-extension-form">
            <div className="session-extension-content">
              <p>Enter your password to extend the session:</p>
              
              <div className="session-field">
                <label htmlFor="extension-username">Username</label>
                <input
                  id="extension-username"
                  type="text"
                  value={username}
                  disabled
                  className="session-input session-input-disabled"
                />
              </div>

              <div className="session-field">
                <label htmlFor="extension-password">Password</label>
                <input
                  id="extension-password"
                  type="password"
                  value={extensionState.password}
                  onChange={handlePasswordChange}
                  onKeyPress={handleKeyPress}
                  disabled={extensionState.isExtending}
                  className="session-input"
                  placeholder="Enter your password"
                  autoComplete="current-password"
                />
              </div>

              {extensionState.error && (
                <div className="session-error">
                  ❌ {extensionState.error}
                </div>
              )}
            </div>

            <div className="session-extension-actions">
              <button
                type="submit"
                disabled={extensionState.isExtending || !extensionState.password.trim()}
                className="session-extend-btn"
              >
                {extensionState.isExtending ? (
                  <>
                    <span className="session-spinner"></span>
                    Extending...
                  </>
                ) : (
                  '🔄 Extend Session'
                )}
              </button>
              
              <button
                type="button"
                onClick={onCancel}
                disabled={extensionState.isExtending}
                className="session-cancel-btn"
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        <div className="session-extension-note">
          <small>💡 Your session will be extended for another 15 minutes</small>
        </div>
      </div>
    </div>
  )
} 